import { Suspense, lazy, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { useSearchParams } from "@/lib/router-compat";
import { usePageTitle } from '@/hooks/usePageTitle';
import { useExternalUrlKey } from '@/hooks/useExternalUrlKey';
import { TableSkeleton } from '@/components/ui/skeletons';
import { ReportViewBanner } from "@/components/deals/report-view-banner";
import { PageHeader } from "@/components/ui/page-header";
import { PageHeaderActionsProvider } from "@/components/ui/page-header-actions";
import { apiClient } from "@/lib/api/client";
import { reportViewToDealsProps } from "@/lib/report-filters";
import type { ReportView } from "@/types/crm";

const DealsTable = lazy(
  () => import('@/components/deals/deals-table').then(m => ({ default: m.DealsTable }))
);

function DealsPage() {
  // useSearchParams requires a Suspense boundary under static export (CSR bail-out).
  return (
    <Suspense fallback={null}>
      <DealsPageInner />
    </Suspense>
  );
}

function DealsPageInner() {
  usePageTitle('Deals');
  const router = useRouter();
  const queryClient = useQueryClient();
  const searchParams = useSearchParams();
  const viewId = searchParams.get("view");
  // Remount the table only when the URL changes externally (agent navigation,
  // history); self-driven filter writes keep the mounted table and its focus.
  const urlKey = `${viewId ?? ""}|${searchParams.toString()}`;
  const { key: tableKey, markSelfNavigation } = useExternalUrlKey(urlKey, searchParams);

  const { data: view, isPending: viewPending } = useQuery({
    queryKey: ["report-view", viewId],
    enabled: Boolean(viewId),
    staleTime: 30_000,
    queryFn: () => apiClient.get<ReportView>(`/api/views/${viewId}`),
  });

  const viewProps = view ? reportViewToDealsProps(view.filterJson) : null;

  const handleQueryParamsChange = useCallback(
    (params: {
      ownerId?: string;
      status?: string;
      dealType?: string;
      q?: string;
    }) => {
      const next = {
        view: searchParams.get("view") ?? undefined,
        ...params,
      };
      markSelfNavigation(next);
      void router.navigate({
        to: "/dashboard/deals",
        search: next as never,
        replace: true,
      });
    },
    [router, searchParams, markSelfNavigation],
  );

  const handleSaveView = useCallback(
    async (name: string) => {
      if (!viewId) return;
      await apiClient.put(`/api/views/${viewId}`, { name, kind: "Saved" });
      await queryClient.invalidateQueries({ queryKey: ["report-view", viewId] });
    },
    [viewId, queryClient],
  );

  const handleClearView = useCallback(() => {
    const next = {
      ownerId: searchParams.get("ownerId") ?? undefined,
      status: searchParams.get("status") ?? undefined,
      dealType: searchParams.get("dealType") ?? undefined,
      q: searchParams.get("q") ?? undefined,
    };
    markSelfNavigation(next);
    void router.navigate({
      to: "/dashboard/deals",
      search: next as never,
      replace: true,
    });
  }, [router, searchParams, markSelfNavigation]);

  if (viewId && viewPending) {
    return (
      <div className="flex flex-col gap-4 flex-1 min-h-0">
        <TableSkeleton rows={8} />
      </div>
    );
  }

  return (
    <PageHeaderActionsProvider>
      <div className="flex flex-col gap-2 flex-1 min-h-0">
        <PageHeader title="Deals" askAi />
        {view?.kind === "Ephemeral" ? (
          <ReportViewBanner
            view={view}
            onSave={handleSaveView}
            onClear={handleClearView}
          />
        ) : null}

        <DealsTable
          key={tableKey}
          initialLayout={searchParams.get("layout") === "board" ? "board" : "table"}
          onLayoutChange={(next) => {
            const params = Object.fromEntries(searchParams.entries());
            void router.navigate({
              to: "/dashboard/deals",
              search: { ...params, layout: next } as never,
              replace: true,
            });
          }}
          initialDealTypeId={viewProps?.dealTypeId ?? searchParams.get("dealType")}
          initialOwnerId={viewProps?.ownerId ?? searchParams.get("ownerId")}
          initialStatus={viewProps?.status ?? searchParams.get("status")}
        initialQuery={viewProps?.q ?? searchParams.get("q")}
        initialCustomFilters={viewProps?.customFilters}
        onQueryParamsChange={handleQueryParamsChange}
      />
      </div>
    </PageHeaderActionsProvider>
  );
}

import { createFileRoute } from '@tanstack/react-router';
export const Route = createFileRoute('/dashboard/deals')({
  component: DealsPage,
  pendingComponent: () => <div className="flex flex-col gap-4 flex-1 min-h-0"><TableSkeleton rows={8} /></div>,
  pendingMs: 0,
  pendingMinMs: 150,
});
